import { FocusTrap } from '@mui/base'
import {
  Button,
  ClickAwayListener,
  DialogActions,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  TextFieldProps,
} from '@mui/material'
import { CalendarIcon, DateCalendar, DateView } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { useRef, useState } from 'react'
import React from 'react'

import { useComplatibleLocale, useUnknownDateInputMask } from '../../hooks'
import { COLORS, createComponentWrapperWithRef, styled, uiTokens } from '../../theme'

const StyledPopper = styled(Popper)`
  z-index: ${({ theme }) => theme.zIndex.modal};
`

const Buttons = styled(DialogActions)`
  border-top: 1px solid ${COLORS.border};
  button:first-child {
    margin-right: auto;
  }
`
const StyledIconButton = createComponentWrapperWithRef(
  {
    mui: IconButton,
    nova: IconButton,
    novaProps: {
      sx: {
        color: uiTokens.uiContent.accent,
      },
    },
  },
  'StyledIconButton',
)

type DateInputLabels = {
  calendarClose: string
  calendarCancel: string
  setCurrentYear: string
  setCurrentMonth: string
  setToday: string
}

function DateInput({
  value,
  onChange,
  accuracy,
  label,
  disabled,
  labels: { setCurrentYear, setCurrentMonth, setToday, calendarClose, calendarCancel },
}: Omit<TextFieldProps, 'onChange' | 'value'> & {
  value: DateTime | null
  onChange: (e: DateTime | null, accuracy: DatePickerUnknownAccuracy) => void
  accuracy: DatePickerUnknownAccuracy
  labels: DateInputLabels
}) {
  const {
    displayValue,
    hasError,
    inputProps,
    inputRef,
    handleKeyDown,
    handleFocus,
    handleClick,
    handleBlur,
    placeholder,
    locale,
  } = useUnknownDateInputMask({
    date: value,
    accuracy,
    onChange,
  })

  const ref = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<DateView>('year')
  const [calendarValue, setCalendarValue] = useState(value)
  const [originalValue, setOriginalValue] = useState(value)
  const [originalAccuracy, setOriginalAccuracy] = useState(accuracy)

  const today = DateTime.fromObject({}, { locale }).startOf('day')

  const onOpen = () => {
    if (disabled) return
    setOriginalValue(value)
    setOriginalAccuracy(accuracy)
    const year = ['year', 'month', 'day'].includes(accuracy) ? value?.year : today.year
    const month = ['month', 'day'].includes(accuracy) ? value?.month : today.month
    const day = ['day'].includes(accuracy) ? value?.day : today.day
    setView('year')
    setCalendarValue(DateTime.fromObject({ year, month, day }, { locale }))
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const handleEscape = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (open && e.key === 'Escape') {
      onClose()
    }
  }

  const handleCalendarChange = (newValue: DateTime | null) => {
    const val = newValue ? newValue : null
    setCalendarValue(val)
    onChange(val?.startOf(view) ?? null, view)
  }

  const handleFocusedViewChange = (newView: DateView, hasFocus: boolean) => {
    if (!hasFocus) {
      return
    }
    setView(newView)
  }

  const setCurrent = () => {
    setCalendarValue(today)
    onChange(today.startOf(view), view)
    onClose()
  }

  const onCancel = () => {
    onChange(originalValue, originalAccuracy)
    onClose()
  }

  return (
    <>
      <TextField
        size="small"
        ref={ref}
        label={label}
        disabled={disabled}
        placeholder={placeholder}
        error={hasError}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <StyledIconButton onClick={onOpen} edge="end" disabled={disabled}>
                <CalendarIcon />
              </StyledIconButton>
            </InputAdornment>
          ),
        }}
        inputProps={inputProps}
        inputRef={inputRef}
        value={displayValue}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onClick={handleClick}
        onBlur={handleBlur}
      />
      <StyledPopper open={open} anchorEl={ref.current} placement="bottom-start">
        <ClickAwayListener onClickAway={onClose}>
          <Paper elevation={4} onKeyDown={handleEscape}>
            <FocusTrap open>
              <div tabIndex={-1}>
                <DateCalendar<DateTime>
                  openTo="year"
                  views={['year', 'month', 'day']}
                  value={calendarValue}
                  onChange={handleCalendarChange}
                  onFocusedViewChange={handleFocusedViewChange}
                  referenceDate={today}
                />
                <Buttons>
                  <Button onClick={setCurrent}>
                    {view === 'year' && setCurrentYear}
                    {view === 'month' && setCurrentMonth}
                    {view === 'day' && setToday}
                  </Button>
                  <Button onClick={onClose}>{calendarClose}</Button>
                  <Button onClick={onCancel}>{calendarCancel}</Button>
                </Buttons>
              </div>
            </FocusTrap>
          </Paper>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}

export type DatePickerUnknownAccuracy = 'unknown' | 'year' | 'month' | 'day'

export type DatePickerUnknownProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value: DateTime | null
  onChange: (e: DateTime | null, accuracy: DatePickerUnknownAccuracy) => void
  accuracy: DatePickerUnknownAccuracy
  locale: string
  labels: DateInputLabels
}

export function DatePickerUnknown(props: DatePickerUnknownProps) {
  const { locale = DateTime.local().locale, ...rest } = props
  const compatibleLocale = useComplatibleLocale(locale)

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={compatibleLocale}>
      <DateInput {...rest} />
    </LocalizationProvider>
  )
}
